import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Zap, Headphones, Star, ArrowRight, CreditCard, Smartphone, CheckCircle, TrendingUp } from 'lucide-react';

// Testimonial images from original site
const testimonialImages = [
  'https://idyllic-blancmange-815b8f.netlify.app/assets/IMG_1009_1754749452263-C_GISA35.jpeg',
  'https://images.pexels.com/photos/3824771/pexels-photo-3824771.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://idyllic-blancmange-815b8f.netlify.app/assets/IMG_1018_1754749371278-ogNXrke4.jpeg',
];

const businessImage1 = 'https://idyllic-blancmange-815b8f.netlify.app/assets/IMG_0991_1754741577093-CelThVBJ.webp';
const businessImage2 = 'https://idyllic-blancmange-815b8f.netlify.app/assets/IMG_1001_1754743682125-XjAdeMQq.webp';

export const Home = () => {
  const { t, translations } = useLanguage();

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative pt-24 lg:pt-32 pb-16 lg:pb-24 overflow-hidden" data-testid="hero-section">
        {/* Background Glow */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-emerald-500/10 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-amber-500/5 rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
                <span className="text-lg">🇪🇺</span>
                <span className="text-sm text-emerald-400 font-medium">{t('hero.badge')}</span>
              </div>

              {/* Heading */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 font-display" data-testid="hero-title">
                {t('hero.title').split(' ').map((word, i) => (
                  word === 'BIG' || word === 'UŠETŘTE' || word === 'SPAREN' || word === 'OSZCZĘDZAJ' || word === 'AHORRA' || word === 'POUPE' ? (
                    <span key={i} className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 to-amber-500">{word} </span>
                  ) : (
                    <span key={i}>{word} </span>
                  )
                ))}
              </h1>

              {/* Subtitle */}
              <p className="text-lg text-zinc-400 mb-8 max-w-xl mx-auto lg:mx-0" data-testid="hero-subtitle">
                {t('hero.subtitle')}
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 text-black font-bold rounded-full hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:-translate-y-0.5"
                  data-testid="hero-cta-primary"
                >
                  {t('hero.cta1')}
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/services"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-amber-500/50 text-amber-400 font-semibold rounded-full hover:bg-amber-500/10 hover:border-amber-500 transition-all"
                  data-testid="hero-cta-secondary"
                >
                  {t('hero.cta2')}
                </Link>
              </div>
            </div>

            {/* Right - Terminal and Card Visual */}
            <div className="relative" data-testid="hero-visual">
              <div className="relative mx-auto max-w-lg flex items-end justify-center gap-[-20px]">
                
                {/* POS Terminal */}
                <div className="relative z-10 transform -rotate-3">
                  <div className="w-48 sm:w-56 bg-gradient-to-b from-emerald-400 to-emerald-500 rounded-3xl p-3 shadow-2xl shadow-emerald-500/30">
                    {/* Terminal Top */}
                    <div className="text-center py-2">
                      <span className="text-white font-bold text-sm italic">CentrixPay</span>
                    </div>
                    
                    {/* Terminal Screen */}
                    <div className="bg-zinc-900 rounded-2xl p-4 mx-1">
                      {/* Amount Display */}
                      <div className="text-emerald-400 text-2xl sm:text-3xl font-bold font-mono mb-1">
                        €247.83
                      </div>
                      <div className="text-zinc-400 text-xs mb-4">EUR</div>
                      
                      {/* App Grid */}
                      <div className="grid grid-cols-3 gap-2 mb-4">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                          <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-amber-500 rounded-xl flex items-center justify-center">
                          <Smartphone className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-500 rounded-xl flex items-center justify-center">
                          <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                          <Headphones className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                        <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                          <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                        </div>
                      </div>
                      
                      {/* Status Bar */}
                      <div className="flex items-center justify-between">
                        <span className="text-zinc-400 text-xs">Balanced</span>
                        <div className="flex items-center gap-1">
                          <span className="text-zinc-400 text-xs">4.5G</span>
                          <div className="flex gap-0.5">
                            <div className="w-1 h-2 bg-zinc-400 rounded-sm"></div>
                            <div className="w-1 h-3 bg-zinc-400 rounded-sm"></div>
                            <div className="w-1 h-4 bg-zinc-400 rounded-sm"></div>
                            <div className="w-1 h-5 bg-emerald-400 rounded-sm"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Terminal Bottom Button */}
                    <div className="flex justify-center py-3">
                      <div className="w-12 h-1.5 bg-zinc-700 rounded-full"></div>
                    </div>
                  </div>
                </div>
                
                {/* Credit Card */}
                <div className="relative -ml-8 sm:-ml-12 z-20 transform rotate-3">
                  <div className="w-56 sm:w-72 h-36 sm:h-44 bg-gradient-to-br from-emerald-400 via-emerald-300 to-amber-400 rounded-2xl p-5 shadow-2xl shadow-emerald-500/20 overflow-hidden">
                    {/* Card Background Pattern */}
                    <div className="absolute inset-0 opacity-30">
                      <div className="absolute top-0 right-0 w-40 h-40 bg-white/20 rounded-full blur-2xl transform translate-x-10 -translate-y-10"></div>
                      <div className="absolute bottom-0 left-0 w-32 h-32 bg-amber-300/30 rounded-full blur-xl transform -translate-x-10 translate-y-10"></div>
                    </div>
                    
                    <div className="relative h-full flex flex-col justify-between">
                      {/* Card Header */}
                      <div className="flex items-start justify-between">
                        <span className="text-zinc-800 font-bold text-lg italic">CentrixPay</span>
                      </div>
                      
                      {/* Chip */}
                      <div className="w-10 h-8 sm:w-12 sm:h-10 bg-gradient-to-br from-amber-400 to-amber-500 rounded-md shadow-inner"></div>
                      
                      {/* Card Details */}
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-zinc-700 text-xs">Card Number</span>
                          <span className="text-zinc-700 text-xs">Expiration</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex gap-2">
                            {[1,2,3,4].map((_, i) => (
                              <div key={i} className="flex gap-0.5">
                                {[1,2,3,4].map((_, j) => (
                                  <div key={j} className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                                ))}
                              </div>
                            ))}
                          </div>
                          <span className="text-zinc-800 font-bold text-2xl sm:text-3xl">∞</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating Glow Effects */}
                <div className="absolute -top-8 -right-8 w-24 h-24 bg-emerald-500/30 rounded-full blur-2xl animate-pulse" />
                <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-amber-500/20 rounded-full blur-2xl animate-pulse delay-700" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 relative" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-display" data-testid="features-title">
              {t('features.title')}
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              {t('features.subtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Image */}
            <div className="relative order-2 lg:order-1">
              <div className="relative rounded-2xl overflow-hidden border border-white/10">
                <img
                  src={businessImage1}
                  alt={t('features.imageAlt')}
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <p className="text-white text-sm">{t('features.imageCaption')}</p>
                </div>
              </div>
            </div>

            {/* Feature Cards */}
            <div className="space-y-6 order-1 lg:order-2">
              {/* Security */}
              <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-emerald-500/50 transition-all duration-300" data-testid="feature-security">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center shrink-0">
                    <Shield className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{t('features.security.title')}</h3>
                    <p className="text-zinc-400 text-sm">{t('features.security.description')}</p>
                  </div>
                </div>
              </div>

              {/* Settlement */}
              <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-emerald-500/50 transition-all duration-300" data-testid="feature-settlement">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center shrink-0">
                    <Zap className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{t('features.settlement.title')}</h3>
                    <p className="text-zinc-400 text-sm">{t('features.settlement.description')}</p>
                  </div>
                </div>
              </div>

              {/* Support */}
              <div className="group bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-emerald-500/50 transition-all duration-300" data-testid="feature-support">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center shrink-0">
                    <Headphones className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">{t('features.support.title')}</h3>
                    <p className="text-zinc-400 text-sm">{t('features.support.description')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 lg:py-24 bg-zinc-900/50" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4 font-display" data-testid="testimonials-title">
              {t('testimonials.title')}
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              {t('testimonials.subtitle')}
            </p>
          </div>

          {/* Business Image */}
          <div className="relative rounded-2xl overflow-hidden border border-white/10 mb-12 max-w-3xl mx-auto">
            <img
              src={businessImage2}
              alt={t('testimonials.imageAlt')}
              className="w-full h-[300px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <p className="text-white text-sm">{t('testimonials.imageCaption')}</p>
            </div>
          </div>

          {/* Testimonial Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {translations.testimonials.reviews.map((review, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-emerald-500/30 transition-all duration-300"
                data-testid={`testimonial-card-${index}`}
              >
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={testimonialImages[index]}
                    alt={review.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-emerald-500/30"
                  />
                  <div>
                    <h4 className="text-white font-semibold">{review.name}</h4>
                    <p className="text-zinc-400 text-sm">{review.role}</p>
                  </div>
                </div>
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-zinc-300 text-sm italic">"{review.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 relative overflow-hidden" data-testid="cta-section">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-emerald-500/10 rounded-full blur-[100px]" />
        </div>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 font-display" data-testid="cta-title">
            {t('cta.title')}
          </h2>
          <p className="text-lg text-zinc-400 mb-8 max-w-2xl mx-auto">
            {t('cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 text-black font-bold rounded-full hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50"
              data-testid="cta-button-primary"
            >
              {t('cta.button1')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white/20 text-white font-semibold rounded-full hover:bg-white/5 hover:border-white/40 transition-all"
              data-testid="cta-button-secondary"
            >
              {t('cta.button2')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
